import React from 'react';

const fmtInr = (n) => `₹${Number(n || 0).toLocaleString('en-IN')}`;

function FieldBlock({ label, value }) {
  return (
    <div className="bg-gray-50 rounded-lg px-3 py-2 min-w-0">
      <div className="text-[10px] text-gray-500 uppercase tracking-wide">{label}</div>
      <div className="text-gray-900 font-medium text-sm mt-0.5 break-all">{value ?? '—'}</div>
    </div>
  );
}

export default function AdminTicketBookingOpsPanel({
  bookingOps,
  opsBookingKeys,
  refundTracker,
  bookingMonitor,
}) {
  if (!bookingOps && !opsBookingKeys) return null;

  const cd = bookingOps?.cancellation_details || {};
  const fb = bookingOps?.fare_breakdown || {};
  const fareKeys = Object.keys(fb).filter((k) => fb[k] != null && fb[k] !== '');

  return (
    <div className="bg-white border border-gray-200 rounded-xl p-4 mb-4 space-y-4" data-testid="ticket-booking-ops-panel">
      <div className="text-[10px] uppercase font-bold text-gray-500 tracking-wide">Operations context</div>

      <div>
        <div className="text-[10px] text-gray-500 uppercase mb-2">Booking IDs</div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-2 text-xs">
          <FieldBlock label="ACF reference" value={opsBookingKeys?.canonical_id || bookingOps?.canonical_id} />
          {bookingOps?.internal_id && <FieldBlock label="System id" value={bookingOps.internal_id} />}
          {bookingOps?.supplier_booking_id && <FieldBlock label="Supplier ref" value={bookingOps.supplier_booking_id} />}
          {bookingOps?.tripjack_booking_id && bookingOps.tripjack_booking_id !== bookingOps.supplier_booking_id && (
            <FieldBlock label="Tripjack id" value={bookingOps.tripjack_booking_id} />
          )}
          {bookingOps?.riya_track_id && <FieldBlock label="Riya track" value={bookingOps.riya_track_id} />}
        </div>
      </div>

      <div>
        <div className="text-[10px] text-gray-500 uppercase mb-2">Payment & fare</div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
          <FieldBlock label="Payment status" value={bookingOps?.payment_status} />
          <FieldBlock label="Payment mode" value={bookingOps?.payment_mode || bookingOps?.payment_method} />
          <FieldBlock label="Total" value={bookingOps?.total_amount != null ? fmtInr(bookingOps.total_amount) : null} />
          <FieldBlock label="Amount paid" value={bookingOps?.amount_paid != null ? fmtInr(bookingOps.amount_paid) : null} />
          {bookingOps?.ssr_amount > 0 && <FieldBlock label="SSR add-ons" value={fmtInr(bookingOps.ssr_amount)} />}
          <FieldBlock label="User type" value={bookingOps?.user_type} />
          <FieldBlock label="Route" value={bookingOps?.route} />
          <FieldBlock label="Booked" value={bookingOps?.created_at ? new Date(bookingOps.created_at).toLocaleString() : null} />
        </div>
        {fareKeys.length > 0 && (
          <div className="mt-2 grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
            {fareKeys.slice(0, 8).map((k) => (
              <FieldBlock
                key={k}
                label={k.replace(/_/g, ' ')}
                value={typeof fb[k] === 'number' ? fmtInr(fb[k]) : String(fb[k])}
              />
            ))}
          </div>
        )}
      </div>

      {(cd.refund_status || refundTracker?.refund_status) && (
        <div>
          <div className="text-[10px] text-gray-500 uppercase mb-2">Cancellation / PG refund</div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
            <FieldBlock label="Refund status" value={(refundTracker?.refund_status || cd.refund_status || '').replace(/_/g, ' ')} />
            <FieldBlock label="Refund method" value={cd.refund_method || refundTracker?.refund_method} />
            <FieldBlock label="Refund amount" value={cd.refund_amount != null ? fmtInr(cd.refund_amount) : null} />
            <FieldBlock label="PG reference" value={cd.pg_reference || refundTracker?.pg_reference} />
            {cd.gross_paid != null && <FieldBlock label="Gross paid" value={fmtInr(cd.gross_paid)} />}
          </div>
        </div>
      )}

      {bookingOps?.supplier_status?.raw && (
        <div>
          <p className="text-[10px] text-gray-500 uppercase mb-2">Supplier status (API)</p>
          <div className="flex flex-wrap gap-2 text-xs">
            <span className="px-2 py-1 rounded bg-indigo-100 text-indigo-800 font-mono font-semibold">
              {bookingOps.supplier_status.raw}
            </span>
            <span className="text-gray-600">{bookingOps.supplier_status.label}</span>
            <span className="text-gray-500">· {bookingOps.supplier_status.canonical}</span>
          </div>
        </div>
      )}

      {(bookingMonitor || bookingOps?.failure_category || bookingOps?.supplier_status_bucket) && (
        <div>
          <div className="text-[10px] text-gray-500 uppercase mb-2">Operational status</div>
          <div className="flex flex-wrap gap-2 text-xs">
            {bookingMonitor?.main_status && (
              <span className="px-2 py-1 rounded bg-blue-100 text-blue-800 font-semibold uppercase">
                Monitor: {bookingMonitor.main_status}
              </span>
            )}
            {bookingMonitor?.sla_breached && (
              <span className="px-2 py-1 rounded bg-rose-100 text-rose-800 font-bold">SLA breached</span>
            )}
            {bookingOps?.failure_category && (
              <span className="px-2 py-1 rounded bg-amber-100 text-amber-900">{bookingOps.failure_category}</span>
            )}
            {bookingOps?.supplier_status_bucket && (
              <span className="px-2 py-1 rounded bg-gray-100 text-gray-800">{bookingOps.supplier_status_bucket}</span>
            )}
            {bookingOps?.tripjack_status && (
              <span className="px-2 py-1 rounded bg-indigo-100 text-indigo-800">TJ: {bookingOps.tripjack_status}</span>
            )}
          </div>
          {bookingMonitor?.sla_label && <p className="text-xs text-gray-600 mt-1">{bookingMonitor.sla_label}</p>}
        </div>
      )}

      {bookingOps?.tickets_summary?.count > 0 && (
        <div className="text-xs text-gray-700">
          <span className="font-medium">{bookingOps.tickets_summary.count} ticket(s)</span>
          {bookingOps.tickets_summary.first_pnr && (
            <span className="ml-2">PNR: <span className="font-mono">{bookingOps.tickets_summary.first_pnr}</span></span>
          )}
          {bookingOps.tickets_summary.first_ticket_number && (
            <span className="ml-2"># <span className="font-mono">{bookingOps.tickets_summary.first_ticket_number}</span></span>
          )}
        </div>
      )}
    </div>
  );
}
